import React from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { ArrowRight, Compass, Target, Sparkles } from "lucide-react";

export default function Landing() {
    return (
        <Layout>
            {/* Hero */}
            <section className="bg-ash-off-white" data-testid="hero-section">
                <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7 fade-in">
                        <div className="text-xs uppercase tracking-widest font-bold text-ash-orange mb-5" data-testid="hero-eyebrow">
                            Free assessment from ASH Interactive
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-ash-black leading-[0.95]" data-testid="hero-headline">
                            Not sure what to do next with your marketing?
                        </h1>
                        <p className="mt-6 text-lg md:text-xl text-ash-gray-dark max-w-xl leading-relaxed" data-testid="hero-subhead">
                            Take the free Marketing Clarity Checkup. In just a few minutes,
                            you'll receive practical recommendations based on your organization,
                            goals and current marketing challenges.
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-3">
                            <Link to="/lead" className="btn-primary" data-testid="start-checkup-btn">
                                Start My Free Checkup
                                <ArrowRight size={18} />
                            </Link>
                            <a
                                href="#how-it-works"
                                className="btn-outline"
                                data-testid="learn-more-btn"
                            >
                                How it works
                            </a>
                        </div>
                        <p className="mt-6 text-sm text-ash-gray-dark max-w-md">
                            Your answers help us generate a more personalized Marketing Clarity Report,
                            which can be emailed to you after review by our team.
                        </p>
                    </div>
                    <div className="md:col-span-5">
                        <div className="bg-ash-black text-white p-8 md:p-10 fade-in" data-testid="hero-side-card">
                            <div className="text-xs uppercase tracking-widest font-bold text-ash-orange mb-4">
                                What you'll get
                            </div>
                            <ul className="space-y-5">
                                <li className="flex gap-4">
                                    <div className="text-ash-orange shrink-0"><Target size={20} /></div>
                                    <div>
                                        <div className="font-bold">A Marketing Clarity Score</div>
                                        <div className="text-sm text-white/70">A 0–100 score across six areas of marketing.</div>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <div className="text-ash-orange shrink-0"><Compass size={20} /></div>
                                    <div>
                                        <div className="font-bold">Your Marketing Profile</div>
                                        <div className="text-sm text-white/70">A short, plain-English picture of where you are today.</div>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <div className="text-ash-orange shrink-0"><Sparkles size={20} /></div>
                                    <div>
                                        <div className="font-bold">2–3 practical next steps</div>
                                        <div className="text-sm text-white/70">Honest recommendations from our team — no jargon.</div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Supporting copy */}
            <section className="bg-white border-t border-ash-gray-light" id="how-it-works" data-testid="supporting-section">
                <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-20">
                    <div className="text-xs uppercase tracking-widest font-bold text-ash-gray-dark mb-4">
                        Designed for community-facing organizations
                    </div>
                    <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-ash-black">
                        Maybe your website feels outdated. Maybe you know you need to post more
                        often but do not know what to say.
                    </h2>
                    <p className="mt-6 text-lg text-ash-gray-dark leading-relaxed">
                        This quick checkup is designed to help small businesses, nonprofits,
                        parks &amp; rec departments, restaurants, local service businesses and
                        education organizations identify where their marketing may need the
                        most attention — and what their team could consider doing next.
                    </p>
                    <div className="mt-10 grid sm:grid-cols-3 gap-6">
                        {[
                            { n: "01", t: "Tell us about you", d: "A short lead form with your basics so we can tailor your results." },
                            { n: "02", t: "Take the checkup", d: "11 quick questions across goals, brand, website and content." },
                            { n: "03", t: "Get clear next steps", d: "Instant Marketing Profile, score and 2–3 honest recommendations." },
                        ].map((s) => (
                            <div key={s.n} className="border border-ash-gray-light bg-ash-off-white p-6">
                                <div className="text-ash-orange font-display font-black text-2xl">{s.n}</div>
                                <div className="font-bold mt-2">{s.t}</div>
                                <div className="text-sm text-ash-gray-dark mt-2">{s.d}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12">
                        <Link to="/lead" className="btn-primary" data-testid="start-checkup-btn-bottom">
                            Start My Free Checkup
                            <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    );
}
